import { memo, useCallback, useState } from 'react';

import styled from 'styled-components';

import { Button, ButtonVariants, ComponentSizes } from '../../..';
// Import the proper type from relative-date-filters
import type { RelativeDateFilterType } from '../../../../../features/worklist/utils/relative-date-filters';
import type { DateRangeFilterValue } from '../../../DateRangeFilter';
import { DateRangeFilter } from '../../../DateRangeFilter';

export interface DateRangeColumnMenuProps {
  /**
   * Title for the date filter modal
   */
  title?: string;

  /**
   * Function to calculate date ranges from filter types
   */
  getDateRange?: (filterType: RelativeDateFilterType, fromDate?: Date | null, toDate?: Date | null) => { startDate: Date; endDate: Date | null };

  /**
   * Current filter value extracted from grid state
   */
  currentFilterValue?: DateRangeFilterValue | null;

  /**
   * Callback when filter changes
   */
  onFilterChange?: (filterValue: DateRangeFilterValue | null) => void;

  /**
   * Grid column menu props for standard sort functionality
   */
  gridMenuProps?: Record<string, unknown>;
}

export const DateRangeColumnMenu = memo<DateRangeColumnMenuProps>(
  ({ title = 'Filter Date Range', getDateRange, currentFilterValue, onFilterChange, gridMenuProps }) => {
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

    const handleOpenDateFilter = useCallback(() => {
      setIsDateFilterOpen(true);
    }, []);

    const handleCloseDateFilter = useCallback(() => {
      setIsDateFilterOpen(false);
    }, []);

    const handleDateFilterChange = useCallback(
      (filterValue: DateRangeFilterValue) => {
        onFilterChange?.(filterValue);
        setIsDateFilterOpen(false);
      },
      [onFilterChange],
    );

    const handleDateFilterClear = useCallback(() => {
      onFilterChange?.(null);
      setIsDateFilterOpen(false);
    }, [onFilterChange]);

    return (
      <>
        <MenuContainer>
          {/* Custom date filter button */}
          <MenuItemContainer>
            <MenuButton onClick={handleOpenDateFilter} variant={ButtonVariants.SECONDARY} size={ComponentSizes.SMALL}>
              📅 {title}
            </MenuButton>
          </MenuItemContainer>

          {currentFilterValue && (
            <MenuItemContainer>
              <ClearButton onClick={handleDateFilterClear} variant={ButtonVariants.SECONDARY} size={ComponentSizes.SMALL}>
                🗑️ Clear Filter
              </ClearButton>
            </MenuItemContainer>
          )}
        </MenuContainer>

        <DateRangeFilter
          isOpen={isDateFilterOpen}
          onClose={handleCloseDateFilter}
          value={currentFilterValue}
          onChange={handleDateFilterChange}
          onClear={handleDateFilterClear}
          title={title}
          size={ComponentSizes.SMALL}
        />
      </>
    );
  },
);

DateRangeColumnMenu.displayName = 'DateRangeColumnMenu';

const MenuContainer = styled.div`
  min-width: 200px;
  padding: ${({ theme }) => theme.space.spacing10};
  background: ${({ theme }) => theme.colors.palette.white};
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const MenuSeparator = styled.hr`
  margin: ${({ theme }) => theme.space.spacing10} 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
`;

const MenuItemContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.space.spacing10};

  &:last-child {
    margin-bottom: 0;
  }
`;

const MenuButton = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  text-align: left;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundDisabled};
  }
`;

const ClearButton = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  color: ${({ theme }) => theme.colors.error};

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundDisabled};
    color: ${({ theme }) => theme.colors.error};
  }
`;
